import { DataThresholding, Tune } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { ReactNode, useEffect, useState } from 'react'

import { Header, Modal } from '../../components'
import { useNavigation } from '../../providers/Navigation/Navigation.provider'
import {
  ProviderUser,
  VacationMessagePayload,
} from '../../providers/ProviderDetails/types'
import { themeColors } from '../../theme'
import { Announcements } from '../Admin/Announcements/Announcements.container'
import { Resources } from '../Admin/Resources/Resources.container'
import { useStyles } from '../Main/Main.styles'

export const MainView = ({
  usePaper = true,
  myProviderDetails,
  updateProviderOnlineStatus,
  children,
  getProviderOnlineStatus,
  createVacationMessage,
  removeVacationMessage,
}: {
  usePaper?: boolean
  myProviderDetails: ProviderUser | null
  updateProviderOnlineStatus: (isOnline: boolean) => Promise<ProviderUser>
  children?: ReactNode
  getProviderOnlineStatus: () => Promise<any>
  createVacationMessage: (payload: VacationMessagePayload) => Promise<any>
  removeVacationMessage: (messageId: number) => Promise<any>
}) => {
  const { classes } = useStyles()
  const { navigation, setNavigation } = useNavigation()

  const [showVacationModal, setShowVacationModal] = useState(false)

  const [showCancelModalMode, setShowCancelModalMode] = useState(false)
  const [vacationMessage, setVacationMessage] = useState({
    startDate: null,
    endDate: null,
  })

  const [vacationStart, setVacationStart] = useState<Dayjs | null>(null)
  const [vacationEnd, setVacationEnd] = useState<Dayjs | null>(null)

  const [loading, setLoading] = useState(false)
  const [removeVacationConfirmation, setRemoveVacationConfirmation] =
    useState(false)

  const getFirstOnlineStatus = async () => {
    const status = await getProviderOnlineStatus()
    setShowCancelModalMode(!status)
  }

  useEffect(() => {
    getFirstOnlineStatus()
  }, [])

  useEffect(() => {
    if (
      myProviderDetails?.accessRoles?.some(
        (role) =>
          role.description.includes('Admin') &&
          !navigation.navigationLinks.some((navLink) =>
            navLink.title.includes('Admin')
          )
      )
    ) {
      const navLinksCopy = [...navigation.navigationLinks]

      navLinksCopy.push(
        {
          Icon: <DataThresholding />,
          title: 'Announcements',
          route: 'announcements',
          View: <Announcements />,
        },
        {
          Icon: <Tune />,
          title: 'Resources',
          route: 'resources',
          View: <Resources />,
        }
      )

      const removeDuplicates = (arr: any, key: string) => {
        const seen = new Map()
        return arr.filter((obj: any) => {
          const value = obj[key]
          if (seen.has(value)) {
            return false
          }
          seen.set(value, true)
          return true
        })
      }

      setNavigation({
        navigationLinks: removeDuplicates(navLinksCopy, 'route'),
        allRoutes: [...navigation.allRoutes],
      })
    }
  }, [myProviderDetails])

  return (
    <div data-testid='main'>
      <Header
        setShowVacationModal={setShowVacationModal}
        pages={navigation!.navigationLinks}
        user={myProviderDetails}
      />
      {myProviderDetails?.accessRoles &&
      myProviderDetails?.accessRoles[0].description === 'Deactivated' ? (
        <h1 style={{ margin: '20vh', textAlign: 'center' }}>
          Your account has been suspended. Please contact your administrator if
          you believe this to be a mistake.
        </h1>
      ) : (
        <>
          <Container className={classes.mainView}>
            {usePaper ? (
              <Paper sx={{ padding: 4, height: '100%', overflow: 'auto' }}>
                <div className={classes.viewWrapper}>
                  <>{children}</>
                </div>
              </Paper>
            ) : (
              <div className={classes.viewWrapper}>
                <>{children}</>
              </div>
            )}
            {
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Modal
                  open={showVacationModal}
                  setOpen={setShowVacationModal}
                  title={'Set Up an Out of Office Message'}
                  width={'60%'}
                  ModalContent={
                    <Box sx={{ width: '92%', margin: 'auto' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          margin: '15px 0',
                          alignItems: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        {myProviderDetails?.vacationMessage && (
                          <Box display={'flex'} alignItems={'center'}>
                            <Typography
                              sx={{
                                fontSize: '16px',
                                lineHeight: '19px',
                                color: themeColors.grayblack,
                                marginRight: '10px',
                              }}
                            >
                              It looks like you have an out of office message
                              from{' '}
                              {dayjs(
                                myProviderDetails.vacationMessage.startDate
                              ).format('MM-DD-YYYY')}{' '}
                              to
                              {dayjs(
                                myProviderDetails.vacationMessage.endDate
                              ).format('MM-DD-YYYY')}
                              .
                            </Typography>

                            {removeVacationConfirmation ? (
                              <>
                                <Typography>
                                  Are you sure to remove this out of office
                                  message?{' '}
                                </Typography>

                                <Button
                                  variant='outlined'
                                  color='secondary'
                                  onClick={() => {
                                    setRemoveVacationConfirmation(false)
                                  }}
                                  sx={{ marginRight: '10px' }}
                                >
                                  Cancel
                                </Button>

                                <Button
                                  variant='contained'
                                  color='error'
                                  onClick={async () => {
                                    setLoading(true)
                                    await removeVacationMessage(
                                      myProviderDetails.vacationMessage
                                        ?.vacationMessageId ?? 0
                                    )
                                    setLoading(false)
                                    setRemoveVacationConfirmation(false)
                                    alert(
                                      'The out of office message has been successfully removed.'
                                    )
                                  }}
                                  sx={{ marginRight: '10px' }}
                                >
                                  {loading ? (
                                    <CircularProgress
                                      size={20}
                                      color='secondary'
                                    />
                                  ) : (
                                    'Remove'
                                  )}
                                </Button>
                              </>
                            ) : (
                              <Button
                                variant='contained'
                                color='error'
                                onClick={() => {
                                  setRemoveVacationConfirmation(true)
                                }}
                                sx={{ marginRight: '10px' }}
                              >
                                Remove Out of Office message
                              </Button>
                            )}
                          </Box>
                        )}

                        {!myProviderDetails?.vacationMessage && (
                          <>
                            <Box
                              display={'flex'}
                              flexDirection={'row'}
                              justifyContent={'center'}
                              alignItems={'center'}
                            >
                              <Typography
                                sx={{
                                  fontSize: '16px',
                                  color: themeColors.grayblack,
                                  margin: '0 10px',
                                }}
                              >
                                Start Date:
                              </Typography>
                              <DatePicker
                                value={vacationStart}
                                onChange={(date) => setVacationStart(date)}
                                renderInput={(params: any) => (
                                  <TextField
                                    {...params}
                                    size='small'
                                    variant='outlined'
                                    sx={{
                                      height: '60px',
                                      width: '100%',
                                      marginRight: 2,
                                    }}
                                  />
                                )}
                              />
                              <Typography
                                sx={{
                                  fontSize: '16px',
                                  color: themeColors.grayblack,
                                  margin: '0 10px',
                                }}
                              >
                                End Date:
                              </Typography>
                              <DatePicker
                                value={vacationEnd}
                                onChange={(date) => setVacationEnd(date)}
                                renderInput={(params: any) => (
                                  <TextField
                                    {...params}
                                    size='small'
                                    variant='outlined'
                                    sx={{ height: '60px', width: '100%' }}
                                  />
                                )}
                              />
                            </Box>

                            <Box display={'flex'}>
                              <Button
                                variant='contained'
                                onClick={() => setShowVacationModal(false)}
                                sx={{ marginRight: '10px' }}
                              >
                                No Thanks
                              </Button>
                              <Button
                                variant='contained'
                                color='error'
                                onClick={async () => {
                                  console.log('running this vacation')
                                  setLoading(true)
                                  await createVacationMessage({
                                    startDate:
                                      vacationStart?.format('YYYY-MM-DD') ?? '',
                                    endDate:
                                      vacationEnd?.format('YYYY-MM-DD') ?? '',
                                  })
                                  setLoading(false)
                                }}
                                sx={{ marginRight: '10px' }}
                              >
                                {loading ? (
                                  <CircularProgress
                                    size={20}
                                    color='secondary'
                                  />
                                ) : (
                                  'Set Out of Office'
                                )}
                              </Button>
                            </Box>
                          </>
                        )}
                      </Box>
                    </Box>
                  }
                />
              </LocalizationProvider>
            }

            {
              <Modal
                open={showCancelModalMode}
                setOpen={setShowCancelModalMode}
                title={'Set yourself to active?'}
                width={'30%'}
                ModalContent={
                  <Box sx={{ width: '92%', margin: 'auto' }}>
                    <Typography
                      sx={{
                        fontSize: '16px',
                        lineHeight: '19px',
                        color: themeColors.grayblack,
                      }}
                    >
                      You are currently set to away, but it looks like you are
                      back. Want to update your availability?
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        margin: '15px 0',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant='contained'
                        onClick={() => setShowCancelModalMode(false)}
                        sx={{ marginRight: '10px' }}
                      >
                        No Thanks
                      </Button>
                      <Button
                        variant='contained'
                        color='error'
                        onClick={async () => {
                          await updateProviderOnlineStatus(true)
                          setShowCancelModalMode(false)
                        }}
                        sx={{ marginRight: '10px' }}
                      >
                        Set to Active
                      </Button>
                    </Box>
                  </Box>
                }
              />
            }
          </Container>
        </>
      )}
    </div>
  )
}
